import { ArrowNarrowLeftIcon, PlusCircleIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import profileIcon from 'assets/images/svgs/profileAvatar.svg';
import UsersPlusSvg from 'assets/svg/icons/users-plus.svg?react';
import CirclesIcon from 'assets/svg/icons/CirclesIcon';
import ExpertGlasses from 'assets/svg/icons/ExpertGlasses';
import Globe from 'assets/svg/icons/globe';
import LogOut from 'assets/svg/icons/LogOut';
import SettingsIcon from 'assets/svg/icons/SettingsIcon';
import { getTeamColor } from 'utils/colors/teamColors';
import classNames from 'classnames';
import AccountableLogo from 'components/atoms/AccountableLogo';
import { Button } from 'components/atoms/Button';
import CountryFlagRounded from 'components/atoms/CountryFlagRounded';
import { Menu } from 'components/atoms/Menu';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import logout from 'hooks/Authentication/useLogout';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import useFeatures from 'hooks/useFeatures';
import {
  useReceivedWorkspaceInvitations,
  useWorkspaceActions,
  useWorkspaces,
} from 'hooks/Workspace';
import { useReceivedAccountantInvitations } from 'hooks/Workspace/useAccountantReceivedInvitations';
import type { FC } from 'react';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from 'react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { IExpert } from 'types/experts.types';
import { indexedLanguages, languagesIndexes } from 'utils/languages';
import layoutContext from '../Layout/layoutContext';
import { type IWorkspace } from 'types/workspace.types';
import useGetExpertLoggedInUserData from 'hooks/Authentication/useGetExpertLoggedInUserData';
import { updateAccountantProfile } from 'api/v3/access/accountantsAuth';
import { getDefaultEmail } from 'utils/accountant';
import { EXPERT_ROUTES } from 'applications/expert/routing/expertRoutes';

const WorkspaceMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchWorkspace } = useWorkspaceActions();
  const { workspaces, selectedWorkspace, isLoading } = useWorkspaces();
  const { data: expert } = useGetExpertLoggedInUserData();
  const { invitationsCount: receivedInvitationsCount } =
    useReceivedWorkspaceInvitations();

  const handleWorkspaceSwitch = (workspace: IWorkspace) => {
    if (selectedWorkspace && workspace._id !== selectedWorkspace?._id) {
      switchWorkspace(workspace._id, workspace.name);
    }
  };

  const getAccountantRole = (workspace: IWorkspace) =>
    workspace.members?.find((member) => member.accountantId === expert?._id)
      ?.role;

  const handleAddWorkspace = () => {
    navigate(EXPERT_ROUTES.WORKSPACE);
  };

  if (isLoading) {
    return (
      <div className="flex items-center border border-gray-300 rounded-xl py-1 px-3">
        <div className="animate-pulse h-6 w-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <Menu
      trigger={({ open }) => (
        <div
          className={`
            flex items-center
            border
            ${open ? 'border-black shadow-inner' : 'border-gray-300'}
            rounded-xl
            py-1
            px-3
            cursor-pointer
            transition
            select-none
            hover:border-black
            group
          `}
        >
          <div className="h-7 flex items-center min-w-0">
            <div className="mr-2 flex-shrink-0">
              <CirclesIcon
                size={20}
                color={getTeamColor(selectedWorkspace?._id || '')}
              />
            </div>
            <span
              className="text-sm mr-2 truncate max-w-[150px]"
              title={selectedWorkspace?.name || 'Select Workspace'}
            >
              {selectedWorkspace?.name || 'Select Workspace'}
            </span>
            <div className="flex-shrink-0">
              {open ? (
                <ChevronUpIcon width={17} className="text-black" />
              ) : (
                <ChevronDownIcon
                  width={17}
                  className="text-gray-400 group-hover:text-black"
                />
              )}
            </div>
          </div>
        </div>
      )}
      contentWrapperClassName="divide-y-0 p-2"
      position="right"
    >
      {workspaces.map((workspace) => (
        <Menu.Group key={workspace._id} className="p-0">
          <Menu.Button
            extendClassNames={`hover:bg-primary-50 rounded-md text-black flex items-center`}
            onClick={() => handleWorkspaceSwitch(workspace)}
          >
            <div className="flex items-center justify-between w-full min-w-0">
              <div className="flex items-center min-w-0 flex-1">
                <div className="mr-2 flex-shrink-0">
                  <CirclesIcon size={20} color={getTeamColor(workspace._id)} />
                </div>
                <div className="flex flex-col items-start min-w-0 flex-1">
                  <span
                    className="text-sm truncate w-full"
                    title={workspace.name}
                  >
                    {workspace.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 capitalize">
                      {getAccountantRole(workspace)}
                    </span>
                    {workspace.members && (
                      <span className="text-xs text-gray-400">
                        {workspace.members.length}{' '}
                        {workspace.members.length <= 1
                          ? t('top_panel.workspaces_select.member', {
                              count: workspace.members.length,
                              defaultValue: 'member',
                            })
                          : t('top_panel.workspaces_select.members', {
                              count: workspace.members.length,
                              defaultValue: 'members',
                            })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {selectedWorkspace?._id === workspace._id && (
                <CheckIcon className="w-5 h-5 flex-shrink-0" />
              )}
            </div>
          </Menu.Button>
        </Menu.Group>
      ))}
      <Menu.Group className="p-0">
        <Menu.Button
          extendClassNames="hover:bg-primary-50 rounded-md text-black flex items-center justify-between gap-1"
          onClick={handleAddWorkspace}
        >
          <div className="flex items-center">
            <PlusCircleIcon className="w-6 h-6 mr-2 text-sm" />
            <span>{t('experts.top_panel.workspaces_select.add')}</span>
          </div>
          {receivedInvitationsCount > 0 && (
            <span className="bg-taxes text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {receivedInvitationsCount > 9 ? '9+' : receivedInvitationsCount}
            </span>
          )}
        </Menu.Button>
      </Menu.Group>
    </Menu>
  );
};

const LanguagesMenu = () => {
  const {
    t,
    i18n: { changeLanguage, resolvedLanguage, language },
  } = useTranslation();
  const availableLanguagesPerCountry = useFeatures(
    'settings.available_languages',
    languagesIndexes,
  ) as number[];
  const { pathname } = useLocation();

  const languages = (
    pathname === '/' ? languagesIndexes : availableLanguagesPerCountry
  ).map((langIndex: number) => indexedLanguages[langIndex]);

  useEffect(() => {
    const selectedLanguage = indexedLanguages.findIndex(
      (key: string) => key === language,
    );
    if (
      pathname !== '/' &&
      !availableLanguagesPerCountry.includes(selectedLanguage)
    ) {
      changeLanguage('en');
    }
  }, [language, availableLanguagesPerCountry, pathname, changeLanguage]);

  return (
    <Menu
      trigger={({ open }) => (
        <div
          className={`
        flex items-center
        border
        ${open ? 'border-black shadow-inner' : 'border-gray-300'}
        rounded-xl
        py-1
        px-3
        cursor-pointer
        transition
        select-none
        hover:border-black
        group
      `}
        >
          <Globe className={'w-7 h-7 mr-2  text-gray-400'} />
          <span className="mr-2 uppercase">{resolvedLanguage}</span>
          {open ? (
            <ChevronUpIcon width={17} className="text-black" />
          ) : (
            <ChevronDownIcon
              width={17}
              className="text-gray-400 group-hover:text-black"
            />
          )}
        </div>
      )}
      position="right"
      contentWrapperClassName="divide-y-0 p-2"
    >
      {languages.map((key: string) => (
        <Menu.Group key={key} className="p-0">
          <Menu.Button
            extendClassNames="hover:bg-primary-50 rounded-md text-black "
            onClick={() => {
              updateAccountantProfile({
                language: key as IExpert['language'],
              }).then(() => {
                window.location.reload();
              });
            }}
          >
            <CountryFlagRounded
              country={key as 'be' | 'de' | 'nl' | 'fr' | 'en'}
              className="w-6 h-6 rounded-full object-cover mr-3"
            />
            <span>{t(key)}</span>
          </Menu.Button>
        </Menu.Group>
      ))}
    </Menu>
  );
};

const ClientInvitesBadge = () => {
  const { totalCount: invitationsCount, isLoading } =
    useReceivedAccountantInvitations();
  const navigate = useCustomNavigate();

  if (isLoading || !invitationsCount || invitationsCount === 0) return null;

  return (
    <Button
      structure="text"
      onClick={() =>
        navigate(EXPERT_ROUTES.CLIENT_INVITES_NOTIFICATION, {
          asDefault: true,
        })
      }
      className="flex items-center justify-center rounded-3xl !py-1 !px-3 cursor-pointer transition select-none bg-taxes-50 hover:bg-taxes-100 group"
    >
      <UsersPlusSvg className="w-6 h-6 text-taxes-700" />
      <span className=" text-taxes-700 text-hase font-bold">
        {invitationsCount > 99 ? '99+' : invitationsCount}
      </span>
    </Button>
  );
};

const AccountMenu = () => {
  const { data: expert } = useGetExpertLoggedInUserData();
  const { t } = useTranslation();

  return (
    <Menu
      trigger={({ open }) => (
        <div
          className={`
            flex items-center
            border
            ${open ? 'border-black shadow-inner' : 'border-gray-300'}
            rounded-xl
            py-1
            px-3
            cursor-pointer
            transition
            select-none
            hover:border-black
            group
          `}
        >
          <img
            src={profileIcon}
            alt="Account Menu"
            className="w-7 h-7 rounded-full mr-2"
          />
          <span className="text-sm">{getDefaultEmail(expert?.emails)}</span>
          {open ? (
            <ChevronUpIcon width={17} className="text-black" />
          ) : (
            <ChevronDownIcon
              width={17}
              className="text-gray-400 group-hover:text-black"
            />
          )}
        </div>
      )}
      contentWrapperClassName="divide-y-0 p-2"
      position="right"
    >
      <Menu.Group className="p-0">
        <Link
          className="flex items-center hover:bg-primary-50 rounded-md p-3 gap-1 !text-black"
          to="/settings/members"
        >
          <SettingsIcon size={24} />
          {t('menu.settings')}
        </Link>

        <Menu.Button
          extendClassNames="flex items-center gap-1 hover:bg-primary-50 rounded-md text-black"
          onClick={() => logout()}
        >
          <LogOut size={24} />

          {t('avatarmenu.logout')}
        </Menu.Button>
      </Menu.Group>
    </Menu>
  );
};

const TopPanel: FC<{ fullWidth?: boolean; isStiky?: boolean }> = ({
  fullWidth,
  isStiky = true,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const layout = useContext(layoutContext);
  const navigate = useCustomNavigate();

  const isCustomerPage = pathname.includes('/customer');
  const isSettingsPage = pathname.includes('/settings');
  const showBackButton = isSettingsPage || isCustomerPage;

  const panelClasses = isStiky
    ? classNames(
        `flex flex-col mb9:flex-row items-center justify-between fixed top-4 right-4 z-30 bg-white py-3 px-4 rounded-xl fancy-shadow`,
        {
          '!left-4': fullWidth,
        },
      )
    : 'flex flex-col mb9:flex-row items-center justify-between z-30 bg-white py-5 px-4 shadow-cardBlur backdrop-blur-md rounded-xl w-full';

  return (
    <header className={panelClasses} style={{ left: layout.sidebarArea }}>
      {showBackButton && (
        <Button
          color="gray"
          structure="primary"
          icon={() => <ArrowNarrowLeftIcon className="w-4 mr-3" />}
          onClick={() => navigate('/', { asDefault: true })}
        >
          <span>{t('appheader.customers_list')}</span>
        </Button>
      )}
      <div className="flex items-center gap-2  flex-row">
        <AccountableLogo
          type="wordV2"
          className="h-[1.75rem] w-auto"
          color="#291547"
        />
        <div className="flex items-center gap-2 whitespace-nowrap">
          <Title
            type={SizesEnum.h6}
            className="text-[1.75rem] text-taxes-400 whitespace-nowrap"
          >
            {t('experts.headerLogo.suffix')}
          </Title>
          <ExpertGlasses size={28} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-0 sm:space-x-4  ">
        <ClientInvitesBadge />
        <WorkspaceMenu />
        <LanguagesMenu />
        <AccountMenu />
      </div>
    </header>
  );
};

export default TopPanel;
